import type { ReactNode } from "react";

import clsx from "clsx";
import Image from "next/image";

import type { ImageMedia } from "@/src/domain/model/ImageMedia";

import { OptionalLink } from "@/src/components/navigation/OptionalLink";
import { HoverTooltip } from "@/src/components/ui/HoverTooltip";
import { Availability } from "@/src/domain/model/Availability";
import { percentFormatter } from "@/src/infrastructure/i18n";

interface ItemsAvailabilityProps {
  readonly availability: Availability;
  readonly items: {
    readonly href?: string;
    readonly id: number;
    readonly image: ImageMedia;
    readonly name: string;
  }[];
  readonly title?: string;
}

const defaultTitles = {
  [Availability.COMING_SOON_LISTED]: "Bientôt en rayon",
  [Availability.COMING_SOON_UNLISTED]:
    "Des rendez-vous sont prévus pour essayer de faire arriver le produit en rayon",
  [Availability.HIGH]: `+ de ${percentFormatter.format(Availability.HIGH / 100)} de chances de trouver`,
  [Availability.LOW]: "Une petite chance de trouver",
  [Availability.MEDIUM]: "De fortes chances de trouver",
  [Availability.ZERO]: "Il n’est pas encore possible de trouver",
};

const backgroundColors = {
  [Availability.COMING_SOON_LISTED]: "bg-[#fdf6d3]",
  [Availability.COMING_SOON_UNLISTED]: "bg-[#eff0ef]",
  [Availability.HIGH]: "bg-[#eaf6ed]",
  [Availability.LOW]: "bg-[#fff0ef]",
  [Availability.MEDIUM]: "bg-[#fdf4e5]",
  [Availability.ZERO]: "bg-[#eff0ef]",
};

const titleTextColors = {
  [Availability.COMING_SOON_LISTED]: "text-[#3f3f3f]",
  [Availability.COMING_SOON_UNLISTED]: "text-text",
  [Availability.HIGH]: "text-[#18753c]",
  [Availability.LOW]: "text-[#b31500]",
  [Availability.MEDIUM]: "text-[#a95511]",
  [Availability.ZERO]: "text-text",
};

const titleBeforeContents = {
  [Availability.COMING_SOON_LISTED]: "before:content-['🌟']",
  [Availability.COMING_SOON_UNLISTED]: "before:content-['🤞']",
  [Availability.HIGH]: "before:content-['🤩']",
  [Availability.LOW]: "before:content-['🧐']",
  [Availability.MEDIUM]: "before:content-['💪']",
  [Availability.ZERO]: "before:content-['😢']",
};

const imageOpacities = {
  [Availability.COMING_SOON_LISTED]: null,
  [Availability.COMING_SOON_UNLISTED]: "opacity-50",
  [Availability.HIGH]: null,
  [Availability.LOW]: null,
  [Availability.MEDIUM]: null,
  [Availability.ZERO]: "opacity-50",
};

export function ItemsAvailability({
  availability,
  items,
  title,
}: ItemsAvailabilityProps): null | ReactNode {
  if (items.length === 0) {
    return null;
  }

  return (
    <section
      className={clsx(
        "flex flex-col items-center gap-6 rounded-lg p-6",
        "md:flex-row md:items-start",
        backgroundColors[availability],
      )}
    >
      <h3
        className={clsx(
          "font-medium-antialiased text-center text-sm",
          titleTextColors[availability],
          "before:mr-1",
          titleBeforeContents[availability],
          "md:mt-2 md:shrink-0 md:basis-1/5",
        )}
      >
        {title ?? defaultTitles[availability]}
      </h3>
      <div
        className={clsx(
          "inline-flex flex-wrap justify-center gap-6",
          "md:justify-start",
        )}
      >
        {items.map((item) => (
          <div
            className="relative inline-block size-20 rounded-full bg-white"
            key={`${item.name}-${item.id}`}
          >
            <OptionalLink {...item}>
              <HoverTooltip content={item.name}>
                <Image
                  alt={item.name}
                  className={clsx(
                    "box-content p-3",
                    imageOpacities[availability],
                  )}
                  height={56}
                  src={item.image.url}
                  width={56}
                />
              </HoverTooltip>
            </OptionalLink>
          </div>
        ))}
      </div>
    </section>
  );
}
